import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PendingInviteResumer from "@/components/PendingInviteResumer";
import { Ripples } from "@/components/ui/ripples";
import Index from "./pages/Index.tsx";
import AuthPage from "./pages/AuthPage.tsx";
import NotFound from "./pages/NotFound.tsx";

const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout.tsx"));
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome.tsx"));
const SchedulePage = lazy(() => import("./pages/dashboard/SchedulePage.tsx"));
const RosterPage = lazy(() => import("./pages/dashboard/RosterPage.tsx"));
const LineupsPage = lazy(() => import("./pages/dashboard/LineupsPage.tsx"));
const AnnouncementsPage = lazy(() => import("./pages/dashboard/AnnouncementsPage.tsx"));
const ChatPage = lazy(() => import("./pages/dashboard/ChatPage.tsx"));
const SettingsPage = lazy(() => import("./pages/dashboard/SettingsPage.tsx"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage.tsx"));
const ClaimInvitePage = lazy(() => import("./pages/ClaimInvitePage.tsx"));
const WelcomePage = lazy(() => import("./pages/WelcomePage.tsx"));
const UpgradePage = lazy(() => import("./pages/dashboard/UpgradePage.tsx"));
const PublicRsvpPage = lazy(() => import("./pages/PublicRsvpPage.tsx"));
const PublicTeamPage = lazy(() => import("./pages/PublicTeamPage.tsx"));
const FilesPage = lazy(() => import("./pages/dashboard/FilesPage.tsx"));
const StatsPage = lazy(() => import("./pages/dashboard/StatsPage.tsx"));
const ResultsPage = lazy(() => import("./pages/dashboard/ResultsPage.tsx"));
const AvailabilityPage = lazy(() => import("./pages/dashboard/AvailabilityPage.tsx"));
const SetupProfilePage = lazy(() => import("./pages/SetupProfilePage.tsx"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage.tsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.tsx"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage.tsx"));
const IndexDe = lazy(() => import("./pages/localized/IndexDe.tsx"));
const IndexFr = lazy(() => import("./pages/localized/IndexFr.tsx"));
const IndexEs = lazy(() => import("./pages/localized/IndexEs.tsx"));
const IndexEt = lazy(() => import("./pages/localized/IndexEt.tsx"));
const IndexFi = lazy(() => import("./pages/localized/IndexFi.tsx"));
const MigrateFromPlayerLineup = lazy(() => import("./pages/MigrateFromPlayerLineup.tsx"));
const PickleballLanding = lazy(() => import("./pages/PickleballLanding.tsx"));
const InstallHelpPage = lazy(() => import("./pages/InstallHelpPage.tsx"));
const JoinTeamPage = lazy(() => import("./pages/JoinTeamPage.tsx"));
const PublicLineupBuilder = lazy(() => import("./pages/PublicLineupBuilder.tsx"));
const AlternativesHub = lazy(() => import("./pages/alternatives/AlternativesHub.tsx"));
const CompetitorComparisonPage = lazy(() => import("./pages/alternatives/CompetitorComparisonPage.tsx"));
const FormationGuidePage = lazy(() => import("./pages/guides/FormationGuidePage.tsx"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Ripples />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <ScrollToTop />
          <PendingInviteResumer />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/de" element={<IndexDe />} />
            <Route path="/fr" element={<IndexFr />} />
            <Route path="/es" element={<IndexEs />} />
            <Route path="/et" element={<IndexEt />} />
            <Route path="/fi" element={<IndexFi />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invite" element={<ClaimInvitePage />} />
           <Route path="/join/:code" element={<JoinTeamPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/r/:token" element={<PublicRsvpPage />} />
            <Route path="/t/:slug" element={<PublicTeamPage />} />
            <Route path="/vs/playerlineup" element={<Navigate to="/alternatives/playerlineup" replace />} />
            <Route path="/vs/spond" element={<Navigate to="/alternatives/spond" replace />} />
            <Route path="/vs/teamsnap" element={<Navigate to="/alternatives/teamsnap" replace />} />
            <Route path="/comparisons" element={<Navigate to="/alternatives" replace />} />
            <Route path="/alternatives" element={<AlternativesHub />} />
            <Route path="/alternatives/:slug" element={<CompetitorComparisonPage />} />
            <Route path="/migrate-from-playerlineup" element={<MigrateFromPlayerLineup />} />
            <Route path="/pickleball" element={<PickleballLanding />} />
            <Route path="/lineup-builder" element={<PublicLineupBuilder />} />
            <Route path="/lineup-builder/:format" element={<PublicLineupBuilder />} />
            <Route path="/guides/:slug" element={<FormationGuidePage />} />
            <Route path="/help/install" element={<InstallHelpPage />} />
            <Route path="/setup-profile" element={
              <ProtectedRoute>
                <SetupProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/welcome" element={
              <ProtectedRoute>
                <WelcomePage />
              </ProtectedRoute>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<DashboardHome />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="availability" element={<AvailabilityPage />} />
              <Route path="roster" element={<RosterPage />} />
              <Route path="lineups" element={<LineupsPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="files" element={<FilesPage />} />
              <Route path="stats" element={<StatsPage />} />
              <Route path="results" element={<ResultsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="upgrade" element={<UpgradePage />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
